import {
    type ReactNode,
    createContext,
    useCallback,
    useEffect,
    useState,
    useMemo,
    useRef
} from "react";
import {
    type ViewStyle,
    StyleSheet,
    Platform,
    View
} from "react-native";
import {
    context
} from "./context";
import {
    useKey
} from "./hooks/useKey";
import {
    type IManagerHandles,
    Manager
} from "./Manager";

interface IHostProps {
    children: ReactNode;
    style?: ViewStyle;
    name?: string;
}

export interface IProvider {
    update(key?: string, children?: ReactNode, name?: string): void;
    mount(children: ReactNode, name?: string): string;
    unmount(key?: string): void;
    name?: string;
}

export const Context = createContext<IProvider | null>(null);

export const Host = ({
    children,
    style,
    name
}: IHostProps): ReactNode => {
    const managerRef = useRef<IManagerHandles>(null);

    const containerRef = useRef<View>(null);

    const [
        hasModal,
        setHasModal
    ] = useState(false);

    useEffect(() => {
        const applyModalState = (items: ReturnType<typeof context.getPortals>) => {
            const isModalOpen = items.some((portal) => portal.isModal && (portal.name ? portal.name === name : !name));

            setHasModal(isModalOpen);

            if(Platform.OS !== "web") {
                return;
            }

            const node = containerRef.current as unknown as HTMLElement | null;

            if(!node || typeof node.toggleAttribute !== "function") {
                return;
            }

            node.toggleAttribute("inert", isModalOpen);
        };

        applyModalState(context.getPortals());

        return context.subscribe(applyModalState);
    }, [
        name
    ]);

    const queue: Array<{
        type: "mount" | "update" | "unmount";
        children?: ReactNode;
        name?: string;
        key: string;
    }> = [];

    const {
        generateKey,
        removeKey
    } = useKey();

    const keyHandlersRef = useRef({
        generateKey,
        removeKey
    });

    useEffect(() => {
        while (queue.length && managerRef.current) {
            const action = queue.pop();

            if (action) {
                switch (action.type) {
                    case "mount":
                        managerRef.current?.mount(action.key, action.children, action.name);
                        break;
                    case "update":
                        managerRef.current?.update(action.key, action.children, action.name);
                        break;
                    case "unmount":
                        managerRef.current?.unmount(action.key);
                        break;
                }
            }
        }
    }, []);

    const mount = useCallback((children: ReactNode, _name?: string): string => {
        const key = keyHandlersRef.current.generateKey();

        const targetName = _name ?? name;

        context.mount(key, children, targetName);

        return key;
    }, [
        name
    ]);

    const update = useCallback((key: string, children: ReactNode, _name?: string): void => {
        const targetName = _name ?? name;

        context.update(key, children, targetName);
    }, [
        name
    ]);

    const unmount = useCallback((key: string): void => {
        context.unmount(key);

        keyHandlersRef.current.removeKey(key);
    }, []);

    const providerValue = useMemo(() => ({
        unmount,
        update,
        mount,
        name
    }), [
        unmount,
        update,
        mount,
        name
    ]);

    return <Context.Provider
        value={providerValue}
    >
        <View
            importantForAccessibility={hasModal ? "no-hide-descendants" : "auto"}
            style={[
                stylesheet.container,
                style
            ]}
            collapsable={false}
            ref={containerRef}
        >
            {children}
        </View>
        <Manager
            ref={managerRef}
            name={name}
        />
    </Context.Provider>;
};

const stylesheet = StyleSheet.create({
    container: {
        pointerEvents: "box-none",
        flex: 1
    }
});
